import {useState} from 'react';
import useTranslation from 'next-translate/useTranslation';

import HelpPanel, {HelpPanelSessionProvider} from '@/components/features/help/HelpPanel';
import {Tabs} from '@/components/ui/Tabs';
import ContactChannels from '../ContactChannels';

const HELP_TAB = 'help';
const CONTACT_TAB = 'contact';

type HelpModalContentProps = {
  /** Frozen when the modal opens, so rotating a phone mid-session cannot remount the panels. */
  isTabletOrDesktop: boolean;
  onClose: () => void;
  onStartSupportMessage: () => void;
  experimentVariation: string;
};

const HelpModalContent = ({
  isTabletOrDesktop,
  onClose,
  onStartSupportMessage,
  experimentVariation,
}: HelpModalContentProps) => {
  const {t} = useTranslation('common');

  const [activeTab, setActiveTab] = useState(HELP_TAB);

  // The panel supplies its own padding since the modal is p-0.
  const contactChannels = (
    <ContactChannels
      className={isTabletOrDesktop ? 'p-8' : 'px-4 py-6'}
      onClose={onClose}
      onStartSupportMessage={onStartSupportMessage}
      experimentVariation={experimentVariation}
    />
  );

  const splitLayout = (
    <div className="flex h-[720px] max-h-[85vh]" data-testid="help-modal-content">
      <div className="min-w-0 basis-[600px] overflow-hidden p-8">
        <HelpPanel experimentVariation={experimentVariation} onChatStart={onClose} />
      </div>
      <div aria-hidden className="w-px shrink-0 bg-peppercorn-100" />
      <div className="min-w-0 basis-[400px] overflow-y-auto">{contactChannels}</div>
    </div>
  );

  const tabbedLayout = (
    <Tabs
      className="flex h-full flex-col"
      data-testid="help-modal-content"
      onValueChange={setActiveTab}
      value={activeTab}
    >
      <div className="flex shrink-0 flex-col gap-4 px-4 pt-6">
        <h2 className="pr-12 text-2xl font-bold text-peppercorn-800">
          {t('components.ContactUs.help.title')}
        </h2>
        <Tabs.List>
          <Tabs.Trigger className="h-14 flex-1 justify-center" value={HELP_TAB}>
            {t('components.ContactUs.help.tabs.help')}
          </Tabs.Trigger>
          <Tabs.Trigger className="h-14 flex-1 justify-center" value={CONTACT_TAB}>
            {t('components.ContactUs.help.tabs.contact')}
          </Tabs.Trigger>
        </Tabs.List>
      </div>

      <Tabs.Content
        className="min-h-0 flex-1 overflow-hidden px-4 pb-6 pt-8 data-[state=inactive]:hidden"
        forceMount
        value={HELP_TAB}
      >
        <HelpPanel
          experimentVariation={experimentVariation}
          onChatStart={onClose}
          showHeading={false}
        />
      </Tabs.Content>
      <Tabs.Content className="min-h-0 flex-1 overflow-y-auto" value={CONTACT_TAB}>
        {contactChannels}
      </Tabs.Content>
    </Tabs>
  );

  return (
    <HelpPanelSessionProvider>
      {isTabletOrDesktop ? splitLayout : tabbedLayout}
    </HelpPanelSessionProvider>
  );
};

export default HelpModalContent;
